import React, { useEffect, useState } from 'react';
import './NotificationToast.css';

export interface Notification {
  id: string;
  type: 'achievement' | 'levelup' | 'quest' | 'loot' | 'info';
  title: string;
  message: string;
  icon?: string;
}

interface NotificationToastProps {
  notifications: Notification[];
  onDismiss: (id: string) => void;
}

const NotificationToast: React.FC<NotificationToastProps> = ({ notifications, onDismiss }) => {
  return (
    <div className="notification-container">
      {notifications.map((notification, index) => (
        <ToastItem
          key={notification.id}
          notification={notification}
          index={index}
          onDismiss={() => onDismiss(notification.id)}
        />
      ))}
    </div>
  );
};

interface ToastItemProps {
  notification: Notification;
  index: number;
  onDismiss: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ notification, index, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), index * 100);

    // Auto-dismiss after 5 seconds
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onDismiss, 300);
    }, 5000);

    return () => clearTimeout(timer);
  }, [index, onDismiss]);

  const getIcon = () => {
    if (notification.icon) return notification.icon;

    switch (notification.type) {
      case 'achievement':
        return '🏆';
      case 'levelup':
        return '⬆️';
      case 'quest':
        return '📜';
      case 'loot':
        return '💎';
      default:
        return 'ℹ️';
    }
  };

  const getClassName = () => {
    const baseClass = 'toast-notification';
    const typeClass = `toast-${notification.type}`;
    const visibleClass = isVisible ? 'toast-visible' : '';
    return `${baseClass} ${typeClass} ${visibleClass}`;
  };

  return (
    <div className={getClassName()} onClick={() => {
      setIsVisible(false);
      setTimeout(onDismiss, 300);
    }}>
      <div className="toast-icon">{getIcon()}</div>
      <div className="toast-content">
        <div className="toast-title">{notification.title}</div>
        <div className="toast-message">{notification.message}</div>
      </div>
      <button className="toast-close" onClick={(e) => {
        e.stopPropagation();
        setIsVisible(false);
        setTimeout(onDismiss, 300);
      }}>×</button>
    </div>
  );
};

export default NotificationToast;
